import { useState, useRef, useEffect } from "react";
import { useSelector } from 'react-redux';
import PrintLink from '../PrintLink/PrintLink';
import HelpModal from '../HelpModal/HelpModal';
import ShareModal from '../ShareModal/ShareModal';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import { ChamProjectsOpenButton} from '../ChamProjects/ChamProjects';
import { postSaveToParent } from '../../../Helpers/PostMessageHelper';

import styles from './TopMenu.module.css';
import closeIcon from './assets/close.svg';
import openIcon from './assets/open.svg';

export default function TopMenu({ extended }) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const menuRef = useRef(null);
    
    const currentView = useSelector(state => state.app.currentView);
    const currentImageUrl = useSelector(state => state.app.currentImageUrl);
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const chosenPattern = useSelector(state => state.app.chosenPattern);
    const patternColors = useSelector(state => state.app.patternColors);
    const selectedRoomscene = useSelector(state => state.app.selectedRoomscene);
    const selectedFloorProduct = useSelector(state => state.app.selectedFloorProduct);
    
    // Dynamic download button text based on current view
    const downloadButtonText = currentView === 'room' ? 'Download Roomview' : 'Download Swatch Image';
    const [downloadBtnStatus, setDownloadBtnStatus] = useState(downloadButtonText);

    extended = true;

    // Update download button text when view changes
    useEffect(() => {
        setDownloadBtnStatus(downloadButtonText);
    }, [downloadButtonText]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    const handleMenuTriggerClick = function () {
        setIsMobileMenuOpen(status => !status);
    }

    const closeMenu = () => {
        setIsMobileMenuOpen(false);
    }

    const handleShareModalButtonClick = function () {
        setIsShareOpen(true);
        closeMenu();
    }

    const handleFeedbackModalButtonClick = function () {
        setIsFeedbackOpen(true);
        closeMenu();
    }

    async function downloadImage(imageSrc, nameOfDownload = 'my-image.jpeg') {
        try {
            const response = await fetch(imageSrc);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blobImage = await response.blob();
            const href = URL.createObjectURL(blobImage);
            
            const anchorElement = document.createElement('a');
            anchorElement.href = href;
            anchorElement.download = nameOfDownload;
            
            document.body.appendChild(anchorElement);
            anchorElement.click();
            document.body.removeChild(anchorElement);
            
            window.URL.revokeObjectURL(href);
            console.log('The image has been downloaded');
        } catch (error) {
            console.error('Error downloading image: ', error);
            throw error;
        }
    }

    const handleDownloadButtonClick = async function () {
        if (!chosenPattern || !patternColors || patternColors.length === 0) {
            console.error('No pattern or colors available for download');
            return;
        }

        const viewType = currentView === 'room' ? 'Roomview' : 'Swatch';
        setDownloadBtnStatus(`LOADING ${viewType.toUpperCase()}...`);

        try {
            let downloadUrl;
            const downloadSize = 3000;

            const defaultRoomSceneSpecifier = chosenPattern.defaultRoomSceneSpecifier;
            if (!defaultRoomSceneSpecifier) {
                console.error('No defaultRoomSceneSpecifier available');
                setDownloadBtnStatus(downloadButtonText);
                return;
            }

            const cutLoopString = defaultRoomSceneSpecifier
                .split(',')
                .map(item => item.trim() === 'L' ? '@LOOP' : '@CUT')
                .join(',');

            const selString = patternColors
                .map(color => `!${color.currentId}`)
                .join(',');

            if (currentView === 'room' && selectedRoomscene) {
                let flooringSpec = 'Mannington\\Products\\Standard_Visualizer\\LVT\\Access\\Simple_Beech_6x39()';
                if (selectedFloorProduct) {
                    flooringSpec = selectedFloorProduct.specifier;
                }
                downloadUrl = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${selectedRoomscene.specifier}(qfactor=(10,4))(SIZE=(${downloadSize},0))(APPLY=(${flooringSpec},${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${cutLoopString})))))&size=${downloadSize}`;
            } else {
                downloadUrl = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${cutLoopString})),FOCUS=(@SIZE[1]/(@PATRES[1]|@RES[1])))&size=${downloadSize}&extra=(SIZE=(?,(@SIZE[1]*@PATRES[2]/@PATRES[1])))`;
            }

            const patternName = chosenPattern.name || style || 'CustomRug';
            const fileName = `${patternName} ${size} ${viewType}.jpg`;
            
            await downloadImage(downloadUrl, fileName);
            setDownloadBtnStatus(downloadButtonText);
            console.log(`${viewType} image downloaded successfully as: ${fileName}`);
        } catch (error) {
            console.error('Error in download function:', error);
            setDownloadBtnStatus(downloadButtonText);
        }
        closeMenu();
    }

    const handleHighResDownload = async function () {
        if (!chosenPattern || !patternColors || patternColors.length === 0) {
            console.error('No pattern or colors available for download');
            return;
        }

        setIsDownloading(true);
        closeMenu();

        try {
            const downloadSize = 4096;

            const defaultRoomSceneSpecifier = chosenPattern.defaultRoomSceneSpecifier;
            if (!defaultRoomSceneSpecifier) {
                console.error('No defaultRoomSceneSpecifier available');
                setIsDownloading(false);
                return;
            }

            const cutLoopString = defaultRoomSceneSpecifier
                .split(',')
                .map(item => item.trim() === 'L' ? '@LOOP' : '@CUT')
                .join(',');

            const selString = patternColors
                .map(color => `!${color.currentId}`)
                .join(',');

            const downloadUrl = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${cutLoopString})),FOCUS=(@SIZE[1]/(@PATRES[1]|@RES[1])))&size=${downloadSize}&extra=(SIZE=(?,(@SIZE[1]*@PATRES[2]/@PATRES[1])))`;

            const patternName = chosenPattern.name || style || 'CustomRug';
            const fileName = `${patternName}.jpg`;
            
            await downloadImage(downloadUrl, fileName);
            console.log(`High-res image downloaded successfully as: ${fileName}`);
        } catch (error) {
            console.error('Error in high-res download function:', error);
        }
        setIsDownloading(false);
    }

    const handleHelpButtonClick = function () {
        setIsHelpOpen(true);
        closeMenu();
    }

    const handleStartOverClick = function () {
        window.location.href = '/';
        closeMenu();
    }

    const handleSaveClick = () => {
        const projectUrl = window.location.href;

        const colors = patternColors.map(c => ({
            index: c.index,
            originalId: c.originalId,
            currentId: c.currentId,
            label: c.label,
            sku: c.sku
        }));

        const saveData = {
            projectUrl: projectUrl,
            style: style,
            size: size,
            rugImageUrl: currentImageUrl,
            pattern: chosenPattern ? {
                id: chosenPattern.id,
                name: chosenPattern.name,
                sku: chosenPattern.sku,
                description: chosenPattern.descriptor,
                specifier: chosenPattern.specifier
            } : null,
            colors: colors
        };

        postSaveToParent(saveData);
        closeMenu();
    };

    return (
        <>
            <section className={styles.topMenu} ref={menuRef}>
                <nav aria-label="Main Navigation">
                    <ul className={!isMobileMenuOpen ? styles.closed : ''}>
                        <li className={styles.menuTrigger}>
                            <button onClick={handleMenuTriggerClick} aria-expanded={isMobileMenuOpen} aria-controls="main-menu">
                                <span aria-hidden="true">
                                    {!isMobileMenuOpen && <img src={openIcon} alt='open icon' />}
                                    {isMobileMenuOpen && <img src={closeIcon} alt='close icon' />}
                                </span>
                            </button>
                        </li>

                        <div className={styles.rightNav}>
                            {extended && (<>
                                <li>
                                    <button type="button" onClick={handleStartOverClick} aria-label="Start Over">
                                        <span>Start Over</span>
                                    </button>
                                </li>

                            
                                <li>
                                    <button type="button" onClick={handleSaveClick} aria-label="Save">
                                        <span>Save</span>
                                    </button>
                                </li>

                                <li className={styles.altNav}>
                                    <button type="button" onClick={handleShareModalButtonClick} aria-label="Share">
                                        <span>Share Project Link</span>
                                    </button>
                                </li>

                                <li className={styles.altNav}>
                                    <button type="button" onClick={handleDownloadButtonClick} aria-label="Download">
                                        <span>{downloadBtnStatus}</span>
                                    </button>
                                </li>

                                {/* <li className={styles.altNav}>
                                    <button type="button" onClick={handleHighResDownload} aria-label="Download 10x10">
                                        <span>Download 10 x 10</span>
                                    </button>
                                </li> */}

                                <li className={styles.altNav}>
                                    <PrintLink onClick={closeMenu} />
                                </li>

                                <li>
                                    <button type="button" onClick={handleFeedbackModalButtonClick} aria-label="Feedback">
                                        <span>Feedback</span>
                                    </button>
                                </li>

                                <li>
                                    <button onClick={handleHelpButtonClick}>Visualizer Help</button>
                                </li>
                            </>)}
                        </div>
                    </ul>
                </nav>
            </section>

            <HelpModal 
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
            />

            <ShareModal 
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
            />

            <FeedbackModal 
                isOpen={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
            />

            {isDownloading && (
                <div className={styles.downloadOverlay}>
                    <div className={styles.downloadModal}>
                        <div className={styles.spinner}></div>
                        <p>Downloading high-resolution image...</p>
                        <p className={styles.downloadSubtext}>This may take a moment</p>
                    </div>
                </div>
            )}
        </>
    );
}
